import each from 'lodash/each'
import reduce from 'lodash/reduce'
import uniq from 'lodash/uniq'
import { deleteMultiNode } from 'actions/nodes'
import { deleteMultiEdge } from 'actions/edges'
import { ignoreDeleted } from 'actions/graph'
import actionTypes from 'constants/actionTypes'
import * as selectNodes from 'selectors/graph/nodes'
import * as selectEdges from 'selectors/graph/edges'
import { NeighborhoodSelector } from 'selectors/graph/neighborhood'
import type { MiddlewareStoreShape, NextFn } from 'types/adapters'

type RootState = import('types/state').RootState
type GraphNode = import('types/domain').GraphNode
type GraphEdge = import('types/domain').GraphEdge

type NeighborhoodAction = {
  type?: string
  error?: {
    response?: {
      status?: number
    }
  }
  payload?: {
    data?: {
      nodes?: GraphNode[]
      edges?: GraphEdge[]
    }
  }
  previousPayload?: {
    focus?: string
    ids?: string[]
    depth?: number
  }
  meta?: {
    previousAction?: {
      meta?: {
        receivedAt?: number
      }
    }
  }
}

type MiddlewareStore = MiddlewareStoreShape<RootState>

export default (store: MiddlewareStore) => (next: NextFn<NeighborhoodAction>) => (action: NeighborhoodAction) => {
  const ret = next(action)
  if (
    action.type === actionTypes.GRAPH.GET_NEIGHBORHOOD.RECEIVE ||
    (action.type === actionTypes.GRAPH.GET_NEIGHBORHOOD.FAIL && action.error?.response?.status === 404)
  ) {
    const focus = action.previousPayload?.focus || ''
    const ids = action.previousPayload?.ids || []
    const depth = action.previousPayload?.depth || 1
    const nodes = action.payload?.data?.nodes || []
    const edges = action.payload?.data?.edges || []
    const nodeMap = reduce(
      nodes,
      (acc: Record<string, GraphNode>, n: GraphNode) => {
        acc[n.id] = n
        return acc
      },
      {}
    )
    const edgeMap = reduce(
      edges,
      (acc: Record<string, GraphEdge>, e: GraphEdge) => {
        acc[e.id] = e
        return acc
      },
      {}
    )
    const state = store.getState()
    const ns = selectNodes.all(state)
    const es = selectEdges.all(state)
    const nEMap = selectEdges.byNodeMap(state)
    const objs = reduce(
      ids,
      (acc: Record<string, Record<string, unknown>>, id: string) => {
        acc[id] = {}
        return acc
      },
      { [`${focus}`]: {} }
    )
    const stillExists = (o: GraphNode | GraphEdge) => {
      const receivedAt = action.meta?.previousAction?.meta?.receivedAt || 0
      return o.local || nodeMap[o.id] || edgeMap[o.id] || ((o.modified || 0) >= receivedAt)
    }
    const neighborhood = new NeighborhoodSelector(ns, es, nEMap, depth, objs, stillExists)
    neighborhood.traverse()
    const skipped = neighborhood.getSkipped()
    store.dispatch(
      deleteMultiNode(
        {
          ids: skipped.nodes,
        },
        true
      )
    )
    if (es[focus] && !edgeMap[focus] && !es[focus].local) {
      skipped.edges.push(focus)
    }
    each(ids, (id: string) => {
      if (es[id] && !edgeMap[id] && !es[id].local) {
        skipped.edges.push(id)
      }
    })
    store.dispatch(
      deleteMultiEdge(
        {
          ids: uniq(skipped.edges),
        },
        true
      )
    )
    store.dispatch(ignoreDeleted())
  }
  return ret
}
